/* ============================================================================
   Lille Bjørn — Cart upsell (“The Atelier Line”, v13.1 + GA4), production build, Oct 2026
   ----------------------------------------------------------------------------
   One 64px object above Checkout (cart drawer and /cart) plus a 70px sticky
   Checkout bar on the phone /cart page. The row always says two things:
     line 1  the goal       “$57.80 to a free engraved tag” · “✓ Engraved tag unlocked”
     line 2  the next step  “Matching harness   $89   unlocks tag”
   with a gold progress bar along its bottom edge.

   THIS FILE DRAWS NO MODAL. Every choice opens a component the theme already
   renders on every page (layout/theme.liquid), through that component's own
   public trigger:
     product offers     #dialogQuickShop.showModal(opener)         Quick Shop
                        (opener carries data-product-url="/products/<handle>?view=quick-view")
     free tag           [data-free-gift-progress-claim]           free-gift-modal.js
   (“Personalise”, “Change engraving” and the order note already live in the cart.)

   Decision ladder, first open step wins (re-run after every cart change):
     UNDO      a piece was just removed: say what it cost, offer Undo (8s)
     CLAIM     order ≥ threshold, no tag yet → the gift modal's claim control
     products  UPGRADE (a walk kit → the all-in-one set, the kit line swapped out)
               · SET (missing leash → harness → collar) · PAIR (the companion)
               · CROSS (a collar for a lone tag) · DOWNSELL (the paid name tag,
               for any cart under the goal) · ROTATE (the other leash style).
               Closing Quick Shop without adding = “not now”: that offer goes
               to the back of the queue and the next one shows. The queue
               cycles; nothing is ever locked away and there is no cap.
     STATUS    what the order earned (only when nothing is left to offer)
   The row is never blank while the cart has items.

   How it stays in sync without owning the cart:
     · <lb-cart-upsell> is a custom element: the theme re-renders the drawer /
       #mainCartContainer after every change, and each new element hydrates
       itself from the cart JSON its Liquid snippet embedded.
     · fetch / XMLHttpRequest calls to /cart/* are observed (the gift modal
       does the same) so changes made by Quick Shop, the gift modal or the
       Personalise popup update the row even if no re-render happens.

   Events for other scripts: document “lb:cart-upsell” CustomEvent, detail
   {action: view|open|add|swap|decline|undo|claim, node, key} (unchanged since v13).
   GA4 measurement: see “analytics” below (lb_upsell_* events, ANALYTICS-GA4.md).
   ========================================================================== */
(() => {
  'use strict';
  if (window.LBCartUpsell) return;

  /* ------------------------------------------------------------------ config
     Defaults; each snippet embeds a JSON block that overrides these
     (threshold, gift ids). Handles use {leather} as a placeholder. */
  const DEFAULTS = {
    thresholdBaseCents: 20000,            // free tag threshold, shop BASE currency (settings.free_gift_threshold × 100)
    giftEnabled: true,
    giftVariantIds: [],                   // settings.free_gift_products → variant ids
    giftAvailable: true,                  // at least one gift variant in stock
    giftImage: '',
    pair: { percent: 10, minQty: 2 },     // the automatic “desave10” discount: collars/leashes/harnesses, 2+ pieces
    undoMs: 8000,
    tagNeverUnlocks: true,                // the paid tag is offered only while it would NOT itself reach the goal (never sell the reward)
    companions: {                         // what the row offers, in the leather the cart already has
      collar: 'vero-classic-collar-{leather}',
      leash: 'classic-leash-{leather}',
      omni: 'omni-leash-{leather}',
      harness: 'vero-harness-{leather}',
      tag: 'ripple-nametag'
    },
    // a walk kit → its all-in-one set (the same collar and leash, plus the harness). Keyed by the kit handle without
    // its leather; anything not listed falls back to “<kit>-vero-harness-{leather}”, the catalogue's own pattern.
    upgrades: {
      'vero-classic-collar-leash': 'vero-classic-collar-leash-harness-{leather}',
      'vero-classic-collar-omni-leash': 'vero-classic-collar-omni-leash-vero-harness-{leather}',
      'vero-wide-collar-classic-leash': 'vero-wide-collar-classic-leash-vero-harness-{leather}',
      'vero-wide-collar-omni-leash': 'vero-wide-collar-omni-leash-vero-harness-{leather}',
      'aeris-classic-collar-leash': 'aeris-classic-collar-leash-vero-harness-{leather}',
      'aeris-classic-collar-omni-leash': 'aeris-classic-collar-omni-leash-vero-harness-{leather}',
      'aeris-wide-collar-classic-leash': 'aeris-wide-collar-classic-leash-vero-harness-{leather}',
      'aeris-wide-collar-omni-leash': 'aeris-wide-collar-omni-leash-vero-harness-{leather}',
      'vero-harness-classic-leash': 'vero-classic-collar-leash-harness-{leather}',          // harness kits: the set adds the classic collar
      'vero-harness-omni-leash': 'vero-classic-collar-omni-leash-vero-harness-{leather}'
    },
    defaultLeather: 'black',
    leathers: ['black', 'red', 'green', 'blue', 'brown'],
    // GA4. The Google & YouTube app already loads gtag.js on every page with this property (send_page_view: false; it
    // sends page_view, add_to_cart, purchase itself). The row's events go to it with send_to, so GA4 hears them once.
    // dataLayer: a GTM-shaped copy ({lb_upsell: null} then {event, lb_upsell}) for a web GTM container, if one is ever added.
    // shopify: Shopify.analytics.publish, for custom pixels. Off: the store's “GTM by FeedArmy” pixel could forward it to the
    // same property, and every event would then count twice. Turn a sink on only when nothing else forwards it to GA4.
    analytics: { enabled: true, measurementId: 'G-Y1BK52L3YX', sinks: { gtag: true, dataLayer: true, shopify: false },
      dataLayerName: 'dataLayer', dwellMs: 1000, visibleRatio: 0.5 },
    copy: {
      // line 1: where the order stands (Roboto 400, 11px, 60% ink). The amount is the one figure in full ink.
      toGoal: '{amount} to your complimentary tag',
      nearGoal: 'Only {amount} to your complimentary tag',
      unlocked: 'Complimentary tag unlocked',
      included: 'Engraved tag included',
      pairGoal: 'Any two pieces, 10% off both',
      pairOn: 'Pair saving applied',
      // the /cart phone bar says it shorter (~150px of text beside Checkout)
      toGoalShort: '{amount} to a free tag',
      nearGoalShort: 'Only {amount} to go',
      unlockedShort: 'Tag unlocked',
      includedShort: 'Tag included',
      // line 2: the curated piece (Lora 15) · its price (tabular) · the value, in brass words
      matching: 'Matching {piece}',
      upgrade: 'Upgrade to the full set',
      omni: 'Hands-free Omni',
      classic: 'Classic leash',
      cross: 'Vero Classic collar',
      downsell: 'Engraved name tag',
      valPair: 'save 10%',
      valSet: '10% off',
      valTag: 'unlocks tag',
      claim: 'Choose your engraved tag',
      // line 2 on the narrowest rows (320px phones, long currencies), only when the row cannot fit the full words
      short: { 'Choose your engraved tag': 'Choose your tag', 'Engraved name tag': 'Name tag', 'Vero Classic collar': 'Classic collar', 'Hands-free Omni': 'Omni leash', 'Engraved tag included': 'Tag included', 'Upgrade to the full set': 'Full set' },
      add: 'Add',
      // undo + the floor
      removed: '{piece} removed',
      lowered: '1 {piece} removed',
      lostBoth: 'Tag and 10% off ended',
      lostTag: 'Tag no longer complimentary',
      lostPair: '10% off ended',
      undo: 'Undo',
      saved: 'You saved {amount}',
      tagNamed: '“{name}” engraved tag',
      tagBlank: 'Blank engraved tag',
      done: 'Ready for checkout'
    }
  };
  const PIECE = { collar: 'collar', leash: 'leash', omni: 'hands-free leash', harness: 'harness', tag: 'name tag',
    kit: 'walk kit', bundle: 'full set', gift: 'engraved tag', other: 'item' };

  /* ---------------------------------------------------------------- helpers */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const fill = (tpl, o) => String(tpl).replace(/\{(\w+)\}/g, (_, k) => (o[k] != null ? o[k] : ''));
  const cap = (s) => String(s).replace(/^./, (c) => c.toUpperCase());
  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const url = (path) => root() + String(path).replace(/^\//, '');
  const emit = (action, detail) => document.dispatchEvent(new CustomEvent('lb:cart-upsell', { bubbles: true, detail: Object.assign({ action }, detail) }));
  const reduceMotion = () => window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Same rate rule as free-gift-modal.js: read live, never cached (IDR etc.).
  function rate() { const r = Number(window.Shopify && Shopify.currency && Shopify.currency.rate); return Number.isFinite(r) && r > 0 ? r : 1; }
  // Same formatter as free-gift-modal.js, so the row and the modal print money identically.
  function money(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') return Shopify.formatMoney(cents);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' }).format(cents / 100);
    } catch (e) { return '$' + (cents / 100).toFixed(2); }
  }
  // Shopify CDN image at a given width (thumbnails are 44px; 2× for retina).
  function img(src, w) { if (!src) return ''; const s = String(src); return s + (s.includes('?') ? '&' : '?') + 'width=' + w; }

  /* --------------------------------------------------------------- session
     `declined` is an ORDER, not a ban: the offers the shopper closed, oldest first. An offer never
     leaves the queue; it only moves behind the ones not yet seen, so the row cycles and never ends. */
  const KEY = 'lb-cart-upsell:v2';
  const mem = (() => { try { return Object.assign({ declined: [] }, JSON.parse(sessionStorage.getItem(KEY) || '{}')); } catch (e) { return { declined: [] }; } })();
  const save = () => { try { sessionStorage.setItem(KEY, JSON.stringify(mem)); } catch (e) { /* private mode: memory only */ } };
  const decline = (key) => { mem.declined = mem.declined.filter((k) => k !== key); mem.declined.push(key); save(); };

  /* ------------------------------------------------------------- catalogue
     Cart lines are recognised by handle, which is systematic in this catalogue:
       vero-classic-collar-blue · classic-leash-blue · omni-leash-blue · vero-harness-blue
       vero-classic-collar-leash-blue (walk kit) · …-collar-…-leash-…-harness-… (all-in-one set)
       ripple-nametag · pebble-nametag · *-dog-name-tag-gift                       */
  function classify(item, cfg) {
    const h = String(item.handle || '');
    const props = item.properties || {};
    if (cfg.giftVariantIds.includes(Number(item.variant_id)) || String(props._free_gift) === 'true' || /-gift$/.test(h)) return 'gift';
    if (/name-?tag/.test(h)) return 'tag';
    const c = /collar/.test(h), l = /leash/.test(h), hn = /harness/.test(h);
    if (c && l && hn) return 'bundle';
    if ((c && l) || (hn && l) || (c && hn)) return 'kit';
    if (c) return 'collar';
    if (hn) return 'harness';
    if (/omni/.test(h) && l) return 'omni';
    if (l) return 'leash';
    return 'other';
  }
  const leatherRe = (cfg) => new RegExp('-(' + cfg.leathers.join('|') + ')(?:-\\d+)?$');
  const leatherOf = (h, cfg) => { const m = String(h).match(leatherRe(cfg)); return m ? m[1] : null; };
  const SINGLE = { collar: 1, leash: 1, omni: 1, harness: 1 };   // the pieces the pair discount applies to (cart kinds and offer slots share these names)

  /** The cart, reduced to what the ladder needs. All amounts in presentment sub-units. */
  function model(cart, cfg) {
    const lines = (cart && cart.items ? cart.items : []).map((it) => Object.assign({}, it, { kind: classify(it, cfg) }));
    const slots = new Set(), leashes = new Set();
    let elQty = 0, gb = 0, elOriginal = 0, leather = null, hasTag = false, gift = null;
    lines.forEach((l) => {
      const h = l.handle || '';
      if (l.kind === 'gift') { gift = l; return; }
      gb += Number(l.final_line_price || 0);
      if (l.kind === 'tag') { hasTag = true; return; }
      if (l.kind === 'other') return;
      if (SINGLE[l.kind]) { elQty += l.quantity; elOriginal += Number(l.original_line_price || l.final_line_price || 0); }
      if (/collar/.test(h)) slots.add('collar');
      if (/harness/.test(h)) slots.add('harness');
      if (/leash/.test(h)) { slots.add('leash'); leashes.add(/omni/.test(h) ? 'omni' : 'classic'); }
      leather = leather || leatherOf(h, cfg); // Shopify lists the newest line first: the piece added last sets the match
    });
    const T = Math.round(cfg.thresholdBaseCents * rate());
    const giftOn = cfg.giftEnabled && cfg.giftVariantIds.length > 0;
    return { lines, slots, leashes, elQty, gb, elOriginal, T, giftOn, unlocked: giftOn && gb >= T, hasGift: !!gift, gift, hasTag,
      kit: lines.find((l) => l.kind === 'kit') || null, leather: leather || cfg.defaultLeather, total: Number(cart && cart.total_price || 0) };
  }

  /* -------------------------------------------------------- product prices
     Offers are priced from /products/<handle>.js (presentment currency, live
     stock). Cached for the session; a product that 404s or is sold out is skipped. */
  const products = {};
  function product(handle) {
    if (!handle) return Promise.resolve(null);
    if (products[handle]) return products[handle];
    let cached = null;
    try { cached = JSON.parse(sessionStorage.getItem('lb-cu-p:' + handle) || 'null'); } catch (e) {}
    if (cached && cached.c === (window.Shopify && Shopify.currency && Shopify.currency.active)) return (products[handle] = Promise.resolve(cached.p));
    products[handle] = fetch(url('products/' + handle + '.js'), { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (!p) return null;
        const avail = (p.variants || []).filter((v) => v.available);
        const slim = { handle: p.handle, title: p.title, url: p.url || '/products/' + p.handle, available: avail.length > 0,
          price: avail.length ? Math.min(...avail.map((v) => v.price)) : p.price, image: p.featured_image || '',
          variants: (p.variants || []).map((v) => ({ id: v.id, available: v.available, options: v.options, price: v.price })), options: p.options };
        try { sessionStorage.setItem('lb-cu-p:' + handle, JSON.stringify({ c: window.Shopify && Shopify.currency && Shopify.currency.active, p: slim })); } catch (e) {}
        return slim;
      })
      .catch(() => null);
    return products[handle];
  }
  const companion = (slot, m, cfg) => fill(cfg.companions[slot] || '', { leather: m.leather });
  /** The all-in-one set that completes a walk kit, in the kit's own leather. */
  function upgradeHandle(kit, cfg) {
    const h = String(kit.handle || ''), leather = leatherOf(h, cfg) || cfg.defaultLeather, base = h.replace(leatherRe(cfg), '');
    return fill(cfg.upgrades[base] || (base + '-vero-harness-{leather}'), { leather });
  }

  /* ----------------------------------------------------------------- engine */
  /** What the order total (gift basis) becomes if one `kind` piece at `price` is added (and `minus` leaves). */
  function project(m, kind, price, cfg, minus) {
    const k = cfg.pair.percent / 100;
    const before = m.elQty >= cfg.pair.minQty;
    const after = SINGLE[kind] ? m.elQty + 1 >= cfg.pair.minQty : before;
    let gb = m.gb - (minus || 0) + (SINGLE[kind] && after ? Math.round(price * (1 - k)) : price);
    if (!before && after) gb -= Math.round(m.elOriginal * k); // the saving switches on for what is already there
    return { gb, pairAfter: after };
  }
  /** Ranked product steps for this cart (keys are stable: they are what the “not now” order remembers). */
  function productSteps(m, cfg) {
    const out = [];
    const add = (node, slot, key, extra) => out.push(Object.assign({ node, slot, key, handle: companion(slot, m, cfg) }, extra || {}));
    if (!m.unlocked) {
      if (m.kit) {
        // A walk kit becomes its all-in-one set: the same collar and leash plus the harness, at the set's own price.
        // The kit line leaves the bag when the set arrives (swapKit), so the shopper never holds both.
        const unit = Math.round(Number(m.kit.final_line_price || 0) / Math.max(1, m.kit.quantity));
        add('UPGRADE', 'bundle', 'upgrade:' + m.kit.handle, { handle: upgradeHandle(m.kit, cfg), minus: unit, swap: { key: m.kit.key, quantity: m.kit.quantity } });
      } else if (m.elQty >= cfg.pair.minQty) {
        ['leash', 'harness', 'collar'].filter((s) => !m.slots.has(s)).forEach((s) => add('SET', s, 'set:' + s));
      } else if (m.elQty === 1) {
        const order = m.slots.has('collar') ? ['leash', 'harness'] : m.slots.has('leash') ? ['collar', 'harness'] : ['leash', 'collar'];
        order.forEach((s) => add('PAIR', s, 'pair:' + s));
      } else if (m.lines.length && m.lines.every((l) => l.kind === 'tag' || l.kind === 'gift')) {
        add('CROSS', 'collar', 'cross:collar');
      }
      if (!m.hasTag && !m.hasGift) add('DOWNSELL', 'tag', 'tag'); // the paid tag, for any cart under the goal
    }
    if (m.leashes.has('classic') && !m.leashes.has('omni')) add('ROTATE', 'omni', 'alt:omni');
    if (m.leashes.has('omni') && !m.leashes.has('classic')) add('ROTATE', 'leash', 'alt:leash');
    return out;
  }
  /** The ladder. Resolves to one step; never null while the cart has items. */
  async function decide(m, cfg) {
    if (!m.lines.length) return { node: 'EMPTY' };
    if (state.undo && state.undo.until > Date.now()) return { node: 'UNDO', key: 'undo' };
    if (m.giftOn && cfg.giftAvailable && m.unlocked && !m.hasGift) return { node: 'CLAIM', key: 'claim' };
    // offers not yet closed first, in the ladder's order; closed ones after them, the one closed longest ago first
    const rank = (k) => mem.declined.indexOf(k);
    const steps = productSteps(m, cfg).map((s, i) => ({ s, i })).sort((a, b) => (rank(a.s.key) - rank(b.s.key)) || (a.i - b.i)).map((x) => x.s);
    for (const s of steps) {
      const p = await product(s.handle);
      if (!p || !p.available) continue;
      const q = project(m, s.slot, p.price, cfg, s.minus); // slot names match SINGLE (collar, leash, omni, harness); 'tag' and 'bundle' are outside the pair discount
      if (s.node === 'DOWNSELL' && cfg.tagNeverUnlocks && m.giftOn && q.gb >= m.T) continue; // never sell the reward: the free tag is the better answer
      return Object.assign({}, s, { product: p, projected: q });
    }
    return { node: 'STATUS', key: 'status' };
  }

  /* ------------------------------------------------------------------ state */
  const state = {
    cart: null,            // the last cart we know (Liquid JSON or /cart.js)
    shown: {},             // gift basis last shown per placement: the bar tweens from it, and crossing the threshold plays the reward
    cfg: null,             // the config of the last row that connected (thresholds, gift ids)
    undo: null,            // {lines, note, piece, image, until}
    pendingQS: null,       // {key, handle, swap, adds} while Quick Shop is open from the row
    adds: 0,               // successful /cart/add calls seen
    inflight: 0,           // /cart/add calls still on their way (the “not now” rule waits for them)
    timer: null
  };

  /* ------------------------------------------------------------------- view
     One object, 64px in every state. Two lines of type, a 2px gold hairline beneath them, one quiet ring.
       line 1   where the order stands   “$57.80 to your complimentary tag”      Roboto 400 11/16 at 60% ink, the amount in full ink
       line 2   the curated next step    “Matching harness   $89  unlocks tag”   Lora 15/20 · tabular price · brass words
       hairline progress under the words: gold fill, a bead where the order is now, a hollow ring at the goal,
                and a pale “ghost” to where this piece would take the order (it brightens on hover)
       ring     a 28px hairline circle with + (or →): the whole tile is the button; the ring says so
     Nothing is filled, boxed or badged: the tile is white with a 1px hairline, as the store's own cards. */
  const ICON = {
    tick: '<svg class="lb-cu-tick" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.3 6.3l2.4 2.4 5-5.4" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    plus: '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6h7M6 2.5v7" stroke="currentColor" stroke-width="1" stroke-linecap="round"/></svg>',
    arrow: '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6h7M7 3.5L9.5 6 7 8.5" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    tag: '<svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="4.6" r="2" fill="none" stroke="currentColor" stroke-width="1"/><circle cx="10" cy="12.4" r="5.4" fill="none" stroke="currentColor" stroke-width="1"/></svg>'
  };
  const pctOf = (gb, m) => (gb >= m.T ? 100 : Math.max(2, Math.min(100, Math.round((gb / m.T) * 100))));
  // whole-unit prices read cleaner in a recommendation: $89, not $89.00 (the gift modal does the same: “a $32 piece”)
  const priceText = (c) => money(c).replace(/([.,])00(?=\D*$)/, '');
  /** Line 1. Near the goal (≤ 25% left) it says “Only”. `short`: the /cart phone bar. */
  function goalHTML(m, cfg, short) {
    const C = cfg.copy;
    if (!m.giftOn) return m.elQty >= cfg.pair.minQty ? ICON.tick + esc(C.pairOn) : esc(C.pairGoal);
    if (m.unlocked) return ICON.tick + esc(m.hasGift ? (short ? C.includedShort : C.included) : (short ? C.unlockedShort : C.unlocked));
    const gap = Math.max(0, m.T - m.gb), near = gap <= m.T * 0.25;
    return fill(esc(near ? (short ? C.nearGoalShort : C.nearGoal) : (short ? C.toGoalShort : C.toGoal)), { amount: '<b>' + esc(money(gap)) + '</b>' });
  }
  /** The hairline: fill from the value last shown, a bead at its tip, a ring at the goal, a ghost to `toGb`. */
  function meterHTML(m, cfg, prev, toGb) {
    const pct = m.giftOn ? pctOf(m.gb, m) : (m.elQty >= cfg.pair.minQty ? 100 : 50);
    const from = prev == null || !m.giftOn ? pct : pctOf(prev, m);
    const ghost = m.giftOn && toGb != null && toGb > m.gb ? pctOf(toGb, m) : pct;
    const vt = !m.giftOn ? goalText(m, cfg) : m.unlocked ? 'Complimentary engraved tag unlocked' : money(Math.max(0, m.T - m.gb)) + ' to a complimentary engraved tag';
    return '<span class="lb-cu-meter" style="--lb-cu-p:' + from + '" role="progressbar" aria-label="Progress to the complimentary engraved tag" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + pct + '" aria-valuetext="' + esc(vt) + '">' +
      '<i class="lb-cu-ghost" style="width:' + ghost + '%"></i><i class="lb-cu-fill" style="width:' + from + '%" data-to="' + pct + '"></i><i class="lb-cu-goal-ring"></i></span>';
  }
  const thumb = (src, extra) => '<span class="lb-cu-th' + (extra || '') + '">' + (src ? '<img src="' + esc(img(src, 80)) + '" alt="" width="40" height="40" loading="lazy">' : '') + '</span>';
  const iconThumb = (svg, extra) => '<span class="lb-cu-th lb-cu-th--icon' + (extra || '') + '">' + svg + '</span>';
  // the free tag shows the gift modal's own packshot, framed as its quiet notice frames it (CSS crop, the file is never
  // re-cut): the design already in the bag, else the modal's default design. No modal on the page → the brass hairline tag.
  function giftPhoto(g) {
    const design = g && String(g.handle || '').split('-')[0].toLowerCase();
    const card = design && document.querySelector('input[data-free-gift-design][value="' + design.replace(/["\\]/g, '') + '"]');
    const el = (card && card.closest('label') && card.closest('label').querySelector('.free-gift-modal__design-thumb img')) ||
      document.querySelector('[data-free-gift-notice] .free-gift-notice__thumb img') ||
      document.querySelector('input[data-free-gift-design]:checked ~ * .free-gift-modal__design-thumb img');
    const src = el && el.getAttribute('src');
    return src ? src.replace(/([?&])width=\d+/, '$1width=240') : '';
  }
  const giftThumb = (g) => {
    const src = giftPhoto(g);
    return src ? '<span class="lb-cu-th is-gift-photo"><img src="' + esc(src) + '" alt="" width="40" height="40" loading="lazy"></span>' : iconThumb(ICON.tag, ' is-gift');
  };
  const ring = (svg, kind) => '<span class="lb-cu-go' + (kind ? ' lb-cu-go--' + kind : '') + '" aria-hidden="true">' + svg + '</span>';
  /** The text column: line 1 (the goal, or the undo's consequence) over line 2 (the piece, its price, its value). */
  let SHORT = {};
  const text = (l1, name, side) => '<span class="lb-cu-t"><span class="lb-cu-goal">' + l1 + '</span><span class="lb-cu-l2"><span class="lb-cu-a"' + (SHORT[name] ? ' data-short="' + esc(SHORT[name]) + '"' : '') + '>' + esc(name) + '</span>' + (side ? '<span class="lb-cu-s">' + side + '</span>' : '') + '</span></span>';

  /** One decided step → the 64px object. */
  function rowHTML(step, m, cfg, prev) {
    const C = cfg.copy, goal = goalHTML(m, cfg);
    SHORT = C.short || {};
    const crossed = m.giftOn && m.unlocked && prev != null && prev < m.T;
    const box = (row) => '<div class="lb-cu-box' + (m.unlocked ? ' is-won' : '') + (m.giftOn && !m.unlocked && m.T - m.gb <= m.T * 0.25 ? ' is-near' : '') + (crossed && !reduceMotion() ? ' is-celebrating' : '') + '">' + row + '</div>';
    const meter = (toGb) => meterHTML(m, cfg, prev, toGb);
    const lastLine = m.lines.find((l) => l.kind !== 'gift') || {}, lastImg = lastLine.image; // newest line first
    switch (step.node) {
      case 'UNDO': {
        const u = state.undo;
        return box('<div class="lb-cu-row is-msg">' + thumb(u.image, ' is-muted') + text(u.note ? '<span class="lb-cu-warn">' + esc(u.note) + '</span>' : goal, u.text, '') +
          '<button type="button" class="lb-cu-link" data-lb-cu="undo" aria-label="' + esc(C.undo + ': ' + u.text + (u.note ? ', ' + u.note : '')) + '">' + esc(C.undo) + '</button>' + meter() + '</div>');
      }
      case 'CLAIM':
        // the gift modal listens for this attribute at document level: a shopper's request is never suppressed
        return box('<button type="button" class="lb-cu-row is-claim" data-free-gift-progress-claim data-lb-cu="claim" aria-haspopup="dialog" aria-label="' + esc(C.unlocked + '. ' + C.claim) + '">' +
          giftThumb() + text(goal, C.claim, '') + ring(ICON.arrow, 'gold') + meter() + '</button>');
      case 'STATUS': {
        // the floor, only when nothing is left to offer: the goal line, and what the order earned, in the store's own words
        const g = m.gift, name = g && g.properties && (g.properties['Front Side Text'] || g.properties._engraving_name);
        const saved = m.lines.reduce((a, l) => a + Math.max(0, Number(l.original_line_price || 0) - Number(l.final_line_price || 0)), 0);
        const head = g ? (name ? fill(C.tagNamed, { name }) : C.tagBlank) : saved > 0 ? fill(C.saved, { amount: priceText(saved) }) : C.done;
        return box('<div class="lb-cu-row is-info">' + (g ? giftThumb(g) : thumb(lastImg, lastLine.kind === 'tag' ? ' is-tag' : '')) + text(goal, head, '') + meter() + '</div>');
      }
      default: { // product steps: the store's Quick Shop opens on the product, as from a product card
        const p = step.product, after = step.projected, { name, val, price } = offerWords(step, m, cfg);
        const side = '<span class="lb-cu-price">' + esc(price) + '</span>' + (val ? '<span class="lb-cu-val">' + esc(val) + '</span>' : '');
        const swap = step.swap ? ' data-swap="' + esc(step.swap.key) + '" data-swap-qty="' + Number(step.swap.quantity) + '" data-fit="short-first"' : '';
        return box('<button type="button" class="lb-cu-row is-offer" data-lb-cu="offer" data-key="' + esc(step.key) + '" data-handle="' + esc(p.handle) + '"' + swap + ' data-product-url="' + esc(qsUrl(p, m)) +
          '" aria-haspopup="dialog" aria-label="' + esc(C.add + ' ' + name + ', ' + price + (val ? ', ' + val : '') + '. ' + goalText(m, cfg) + '. Opens Quick Shop') + '">' +
          thumb(p.image, step.slot === 'tag' ? ' is-tag' : '') + text(goal, name, side) + ring(ICON.plus) + meter(after.gb) + '</button>');
      }
    }
  }
  /** A product step's words: the piece, its price, its value in brass words. `benefit` is the same value for analytics. */
  function offerWords(step, m, cfg) {
    const C = cfg.copy, p = step.product, after = step.projected, piece = PIECE[step.slot] || step.slot;
    let name, val = '', price = priceText(p.price);
    const unlocks = m.giftOn && !m.unlocked && after.gb >= m.T;
    if (step.node === 'PAIR') { name = fill(C.matching, { piece }); val = unlocks ? C.valTag : C.valPair; }
    else if (step.node === 'SET') { name = fill(C.matching, { piece }); val = unlocks ? C.valTag : C.valSet; }
    else if (step.node === 'UPGRADE') { name = C.upgrade; val = unlocks ? C.valTag : C.valSet; if (step.minus > 0 && p.price > step.minus) price = '+' + priceText(p.price - step.minus); } // what the set costs beyond the kit
    else if (step.node === 'ROTATE') { name = step.slot === 'omni' ? C.omni : C.classic; if (after.pairAfter) val = C.valSet; }
    else if (step.node === 'CROSS') { name = C.cross; if (unlocks) val = C.valTag; }
    else name = C.downsell;
    return { name, val, price, benefit: !val ? 'none' : val === C.valTag ? 'unlocks_gift' : 'pair_discount' };
  }
  const goalText = (m, cfg) => { const d = document.createElement('div'); d.innerHTML = goalHTML(m, cfg); return d.textContent; };
  /** Quick Shop's opener URL. Preselects the width the cart already has, when this product comes in it, and only
   *  for products without a size: the Fitted picker reads ?variant= as the shopper's own size choice, so the cart
   *  never names a size for them (same option names as snippets/lb-fitted-sized.liquid). */
  const SIZE_OPTION = /^(size|collar size|harness size)$/;
  function qsUrl(p, m) {
    const sized = (p.options || []).some((o) => SIZE_OPTION.test(String((o && o.name) || o).trim().toLowerCase()));
    if (sized) return p.url.split('?')[0] + '?view=quick-view';
    const widths = new Set();
    m.lines.forEach((l) => (l.options_with_values || []).forEach((o) => { if (/width/i.test(o.name)) widths.add(o.value); }));
    const v = (p.variants || []).find((x) => x.available && (x.options || []).some((o) => widths.has(o)));
    return p.url.split('?')[0] + (v ? '?variant=' + v.id + '&view=quick-view' : '?view=quick-view');
  }

  /** Long currencies on small screens (IDR 1,625,000 beside “Matching harness”): the row keeps its 64px and gives up detail in a
   *  fixed order, measured, so the piece's name is never cut: the brass value words, then the price (Quick Shop shows it), and
   *  line 1 falls back to its short form. USD rows fit and are untouched. */
  function fitRow(host, shortGoal) {
    const row = host.querySelector('.lb-cu-row'); if (!row) return;
    const over = (el) => !!el && el.scrollWidth > el.clientWidth + 1;
    row.classList.remove('is-tight', 'is-tighter');
    const a = row.querySelector('.lb-cu-a');
    if (row.dataset.fit === 'short-first') {
      // the set upgrade keeps its value words (“unlocks tag”) longest: its long name goes first, then the price (Quick Shop shows it), the brass words last
      if (over(a) && a.dataset.short) a.textContent = a.dataset.short;
      if (over(a)) row.classList.add('is-tighter');
      if (over(a)) row.classList.add('is-tight');
    } else if (over(a)) { row.classList.add('is-tight'); if (over(a)) row.classList.add('is-tighter'); if (over(a) && a.dataset.short) a.textContent = a.dataset.short; }
    const g = row.querySelector('.lb-cu-goal');
    if (shortGoal && over(g)) g.innerHTML = shortGoal;
  }

  /* -------------------------------------------------------------- analytics
     GA4 measurement of the row's funnel. It never draws, never fetches and never delays the row: each call is a few property
     reads and one push, inside try/catch, so analytics can never break the cart. Event names (ANALYTICS-GA4.md has the schema):
       lb_upsell_view         an offer was SEEN: ≥50% of the row on screen, the drawer open and the tab visible, for 1s. Once per
                              offer, placement and session; an offer that was declined or accepted counts again when it returns.
       lb_upsell_open         the shopper tapped it (Quick Shop, or the gift modal for the free tag)
       lb_upsell_decline      Quick Shop / the gift modal closed without the piece being added (“not now”)
       lb_upsell_accept       the offered piece is in the cart. value = what the cart total actually rose by (a kit upgrade: after
                              the kit line left; a pair: after the 10% switched on for both)
       lb_upsell_undo         Undo brought a removed piece back. value = what the order total regained
       lb_threshold_unlocked  the order crossed the complimentary-tag goal in this session (once)
       lb_gift_claimed        the free engraved tag arrived in the cart (once per session). The engraving itself is never sent.
     No standard add_to_cart: the Google & YouTube app already sends one for every add, the row's included. */
  const VERSION = '2026.10.15';
  const OFFER_TYPE = { PAIR: 'pair', SET: 'set', UPGRADE: 'upgrade_walk_kit', CROSS: 'cross_collar', DOWNSELL: 'downsell_tag', ROTATE: 'rotate_leash', CLAIM: 'claim_gift', UNDO: 'undo' };
  const SLOT_NAME = { drawer: 'drawer', 'page-top': 'page_top', 'page-summary': 'page_summary' };
  const major = (c) => Math.round(Number(c) || 0) / 100;          // Shopify sub-units → the amount GA4 expects (presentment currency)
  const GA = (() => {
    const LKEY = 'lb-cu-ga:v1', log = [], waiting = [];
    let L = null, waitT = null, waited = 0, restoringUntil = 0, pending = null, pendingT = null;
    const A = () => (state.cfg || DEFAULTS).analytics || DEFAULTS.analytics;
    const debug = (() => { try { if (/[?&]lb_cu_debug=1\b/.test(location.search)) sessionStorage.setItem('lb-cu-debug', '1'); return sessionStorage.getItem('lb-cu-debug') === '1'; } catch (e) { return false; } })();
    // The session ledger: impressions already counted, and the last cart state seen (so a reload between two carts, as the gift
    // modal does on /cart, still shows the change). {seen, base, u: unlocked, g: has gift, tb: total, th/gc: fired, pc: claim from the row}
    const ledger = () => L || (L = (() => { try { return JSON.parse(sessionStorage.getItem(LKEY) || 'null'); } catch (e) { return null; } })() || { seen: {} });
    const keep = () => { try { sessionStorage.setItem(LKEY, JSON.stringify(L)); } catch (e) { /* private mode: this page only */ } };
    const currency = () => (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    const where = () => { const d = document.querySelector('sht-cart-drwr'); return d && d.hasAttribute('open') ? 'drawer' : document.body.classList.contains('template-cart') ? 'cart_page' : 'page'; };
    const countOf = (cart, h) => (cart && cart.items || []).reduce((a, i) => a + (i.handle === h ? Number(i.quantity) || 0 : 0), 0);
    // slot|offer_id, plus #<until> for an undo prompt (each removal is its own prompt). Offer ids contain “:”, never “|” or “#”.
    const impKey = (p) => p.placement_slot + '|' + p.offer_id + (p._u ? '#' + p._u : '');
    const forget = (id) => { const s = ledger().seen; Object.keys(s).forEach((k) => { if (k.split('|')[1].split('#')[0] === id) delete s[k]; }); keep(); };

    function gtagSend(name, p) {
      if (typeof window.gtag === 'function') { window.gtag('event', name, p); return; }
      // the app's pixel defines gtag a moment after the page starts: hold the events (never more than 20s)
      waiting.push([name, p]); if (waitT) return;
      (function poll() { waitT = setTimeout(() => { waited += 250;
        if (typeof window.gtag === 'function') { waitT = null; waiting.splice(0).forEach((w) => window.gtag('event', w[0], w[1])); return; }
        if (waited >= 20000) { waitT = null; waiting.length = 0; return; } poll(); }, 250); })();
    }
    function send(name, params) {
      try {
        const a = A(); if (!a.enabled) return;
        const p = {};
        Object.keys(params || {}).forEach((k) => { const v = params[k]; if (k[0] !== '_' && v != null && v !== '' && !(typeof v === 'number' && !Number.isFinite(v))) p[k] = v; });
        p.currency = currency(); p.upsell_version = VERSION;
        if (debug) { p.debug_mode = true; try { console.info('[lb-cart-upsell] ' + name, p); } catch (e) {} }
        log.push({ event: name, params: p, t: Date.now() }); if (log.length > 200) log.shift();
        const s = a.sinks || {};
        if (s.dataLayer) { const dl = (window[a.dataLayerName || 'dataLayer'] = window[a.dataLayerName || 'dataLayer'] || []); dl.push({ lb_upsell: null }); dl.push({ event: name, lb_upsell: Object.assign({}, p) }); }
        if (s.shopify && window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') Shopify.analytics.publish(name, Object.assign({}, p));
        if (s.gtag && a.measurementId) gtagSend(name, Object.assign({ send_to: a.measurementId }, p));
      } catch (e) { /* never the cart's problem */ }
    }

    /** What the row shows, as GA4 parameters. Null for the floor (STATUS) and an empty cart: nothing is offered there. */
    function offer(step, m, cfg, placement) {
      const type = OFFER_TYPE[step.node]; if (!type) return null;
      const p = { offer_type: type, offer_id: step.key, placement: placement === 'drawer' ? 'drawer' : 'cart_page', placement_slot: SLOT_NAME[placement] || placement,
        cart_total_before: major(m.total), cart_item_count: m.lines.reduce((a, l) => a + (l.kind === 'gift' ? 0 : Number(l.quantity) || 0), 0),
        gift_gap: m.giftOn ? major(Math.max(0, m.T - m.gb)) : 0, declines_in_session: mem.declined.length, _total: m.total };
      if (step.node === 'UNDO') {
        const u = state.undo || {};
        return Object.assign(p, { offered_product_handle: u.handle, offered_product_price: major((u.unit || 0) * (u.qty || 1)), offer_piece: classify({ handle: u.handle || '' }, cfg),
          leather_color: leatherOf(u.handle || '', cfg) || 'none', offer_benefit: u.lost || 'none', cart_total_after: major(u.totalBefore != null ? u.totalBefore : m.total), _u: u.until });
      }
      if (step.node === 'CLAIM') return Object.assign(p, { offer_piece: 'gift', offered_product_price: 0, leather_color: 'none', offer_benefit: 'free_gift', cart_total_after: major(m.total) });
      const w = offerWords(step, m, cfg);
      return Object.assign(p, { offered_product_handle: step.product.handle, offered_product_price: major(step.product.price), offer_piece: step.slot,
        leather_color: leatherOf(step.product.handle, cfg) || 'none', offer_benefit: w.benefit,
        cart_total_after: major(m.total + (step.projected.gb - m.gb)) }); // projected: the cart total once this piece is in (the 10% included)
    }

    /* Impressions: armed on every render, on the drawer opening, on scroll into view and on the tab returning; counted when the
       row is still on screen `dwellMs` later. A closed drawer is visibility:hidden + opacity 0 (its geometry still overlaps the
       viewport), so CSS visibility is checked, not only the intersection. */
    function onScreen(host) {
      if (document.visibilityState === 'hidden') return false;
      const el = host.querySelector('.lb-cu-box') || host;
      if (typeof el.checkVisibility === 'function') { if (!el.checkVisibility({ opacityProperty: true, visibilityProperty: true, checkOpacity: true, checkVisibilityCSS: true })) return false; }
      else { for (let n = el; n && n.nodeType === 1; n = n.parentElement) { const s = getComputedStyle(n); if (s.display === 'none' || s.visibility === 'hidden' || Number(s.opacity) === 0) return false; } }
      const r = el.getBoundingClientRect(); if (!r.width || !r.height) return false;
      const w = Math.max(0, Math.min(r.right, innerWidth) - Math.max(r.left, 0)), h = Math.max(0, Math.min(r.bottom, innerHeight) - Math.max(r.top, 0));
      return (w * h) / (r.width * r.height) >= (A().visibleRatio || 0.5);
    }
    function count(host, p) { const k = impKey(p), s = ledger().seen; if (s[k]) return; s[k] = 1; keep(); send('lb_upsell_view', p); }
    function arm(host) {
      try {
        clearTimeout(host._lbGaT); const p = host._lbImp; if (!p || ledger().seen[impKey(p)]) return;
        if (!host._lbIo && 'IntersectionObserver' in window) {
          host._lbIo = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) arm(host); else clearTimeout(host._lbGaT); }), { threshold: [0, 0.5, 1] });
          host._lbIo.observe(host);
        }
        const k = impKey(p);
        host._lbGaT = setTimeout(() => { try { const now = host._lbImp; if (now && impKey(now) === k && host.isConnected && onScreen(host)) count(host, now); } catch (e) {} }, A().dwellMs || 1000);
      } catch (e) {}
    }
    const armAll = () => document.querySelectorAll('lb-cart-upsell').forEach((h) => arm(h));
    let booted = false;
    function boot() {
      if (!booted) { booted = true;
        document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') armAll(); });
        document.addEventListener('free-gift:added', (e) => cart(e.detail && e.detail.cart)); }
      const d = document.querySelector('sht-cart-drwr');
      if (d && !d.__lbCuGa) { d.__lbCuGa = true; new MutationObserver(() => { if (d.hasAttribute('open')) armAll(); }).observe(d, { attributes: true, attributeFilter: ['open'] }); }
      const gm = document.querySelector('free-gift-modal');
      if (gm && !gm.__lbCuGa) { gm.__lbCuGa = true; let shown = false;
        // the free tag opened from the row and closed without a tag in the cart = “not now” on the claim
        new MutationObserver(() => { if (!gm.hidden) { shown = true; return; } if (!shown) return; shown = false;
          setTimeout(() => { try { const l = ledger(); if (!l.pc || model(state.cart, state.cfg || readConfig(document.createElement('div'))).hasGift) return;
            send('lb_upsell_decline', Object.assign({}, l.pc.p, { declines_in_session: mem.declined.length })); l.pc = null; forget('claim'); keep(); } catch (e) {} }, 1200);
        }).observe(gm, { attributes: true, attributeFilter: ['hidden'] }); }
    }

    /** The shopper tapped the row. A tap is proof it was seen: an impression not yet counted is counted first. */
    function open(host) {
      try {
        const p = host && host._lbImp; if (!p) return null;
        if (!ledger().seen[impKey(p)]) count(host, p);
        send('lb_upsell_open', p);
        if (p.offer_type === 'claim_gift') { ledger().pc = { p: Object.assign({}, p), until: Date.now() + 120000 }; keep(); } // survives /cart's reload after the claim
        return Object.assign({}, p, { _count: countOf(state.cart, p.offered_product_handle) });
      } catch (e) { return null; }
    }
    function decline(p) { if (!p) return; send('lb_upsell_decline', Object.assign({}, p, { declines_in_session: mem.declined.length })); forget(p.offer_id); }
    /** Quick Shop added something after a tap on the row: the accept is sent when the cart shows the offered piece (and, for a
     *  kit upgrade, the kit line gone), so cart_total_after and value are the cart's own figures. */
    function expect(p, swap) {
      if (!p) return;
      clearTimeout(pendingT); pending = { p, swap };
      pendingT = setTimeout(() => { if (pending && pending.p === p) settle(state.cart, true); }, 8000);
      settle(state.cart, false);
    }
    function settle(c, late) {
      if (!pending || !c) return;
      const { p, swap } = pending; if (countOf(c, p.offered_product_handle) <= (p._count || 0)) { if (late) pending = null; return; }
      const kit = swap && (c.items || []).find((i) => i.key === swap.key);
      if (swap && kit && kit.quantity >= swap.quantity && !late) return; // the set is in, the kit not yet out: wait for the swap
      pending = null; clearTimeout(pendingT);
      send('lb_upsell_accept', Object.assign({}, p, { cart_total_after: major(c.total_price), value: major(Number(c.total_price || 0) - p._total) }));
      forget(p.offer_id);
    }
    function undo(host, u) {
      try {
        restoringUntil = Date.now() + 6000;
        const p = (host && host._lbImp && host._lbImp.offer_type === 'undo') ? host._lbImp : null;
        const before = u.totalAfter != null ? u.totalAfter : (state.cart && state.cart.total_price) || 0, after = u.totalBefore != null ? u.totalBefore : before;
        send('lb_upsell_undo', Object.assign({}, p || { offer_type: 'undo', offer_id: 'undo', placement: where() === 'drawer' ? 'drawer' : 'cart_page' },
          { offered_product_handle: u.handle, cart_total_before: major(before), cart_total_after: major(after), value: major(after - before), restored_gift: u.gift ? 'yes' : 'no' }));
      } catch (e) {}
    }
    /** Every cart the script learns of (a page's embedded cart, each /cart read). Sends the threshold and gift events on a change
     *  against the last cart this session saw; the first cart of a session is only a baseline (a cart from yesterday is not news). */
    function cart(c) {
      try {
        if (!c || !c.items) return;
        settle(c, false);
        const cfg = state.cfg || readConfig(document.createElement('div')), m = model(normCart(c), cfg), l = ledger();
        const was = JSON.stringify([l.base, l.u, l.g, l.tb, l.th, l.gc, !!l.pc]);
        if (!l.base) { l.base = 1; l.u = m.unlocked; l.g = m.hasGift; l.tb = m.total; keep(); return; }
        const restoring = restoringUntil > Date.now();
        if (m.giftOn && m.unlocked && !l.u && !l.th) {
          l.th = 1;
          const via = pending || state.pendingQS ? 'upsell' : restoring ? 'undo' : 'other';
          send('lb_threshold_unlocked', { threshold_value: major(m.T), cart_total_before: major(l.tb), cart_total_after: major(m.total), unlock_source: via,
            offer_type: via === 'upsell' ? (pending ? pending.p.offer_type : state.pendingQS && state.pendingQS.ga && state.pendingQS.ga.offer_type) : null, placement: where() });
        }
        if (m.hasGift && !l.g && !restoring) {
          const fromRow = !!(l.pc && l.pc.until > Date.now());
          if (!l.gc) {
            l.gc = 1;
            const g = m.gift, P = g.properties || {}, front = P['Front Side Text'] || P._engraving_name, back = P['Back Side Text'] || P._engraving_phone;
            const blank = String(P._engraving_blank) === 'true' || (!front && !back);
            const size = g.variant_title || ((g.options_with_values || [])[0] || {}).value;
            send('lb_gift_claimed', { gift_design: String(g.handle || '').split('-')[0] || null, gift_size: size, engraving_type: blank ? 'blank' : front && back ? 'name_and_phone' : front ? 'name' : 'phone',
              gift_trigger: fromRow ? 'upsell_row' : 'gift_modal', cart_total_after: major(m.total), placement: fromRow ? l.pc.p.placement : where() });
          }
          if (fromRow) { send('lb_upsell_accept', Object.assign({}, l.pc.p, { cart_total_after: major(m.total), value: 0 })); forget('claim'); }
          l.pc = null;
        }
        l.u = m.unlocked; l.g = m.hasGift; l.tb = m.total;
        if (JSON.stringify([l.base, l.u, l.g, l.tb, l.th, l.gc, !!l.pc]) !== was) keep();
      } catch (e) {}
    }
    return { send, offer, arm, boot, open, decline, expect, settle, undo, cart, log,
      ledger: () => ledger(), reset: () => { L = { seen: {} }; keep(); log.length = 0; pending = null; clearTimeout(pendingT); restoringUntil = 0; } };
  })();

  /* --------------------------------------------------------- custom element */
  class LBCartUpsell extends HTMLElement {
    connectedCallback() {
      this.cfg = state.cfg = readConfig(this);
      // A freshly connected row was just rendered by Liquid (page load or the theme's section re-render after a change),
      // so the cart it embeds is the newest truth.
      const embedded = readJSON(this, '[data-lb-cu-cart]');
      if (embedded) { state.cart = normCart(embedded); GA.cart(state.cart); }
      const last = LAST[this.dataset.placement || 'row'], host = $('[data-lb-cu-row]', this);
      if (last && host && embedded && embedded.item_count > 0) { host.innerHTML = last.html; this._sig = last.sig; this._key = last.key; }
      this.addEventListener('click', onRowClick);
      // a new width (rotation, a resized window) re-decides what fits
      if ('ResizeObserver' in window && !this._ro) { let w = 0, t = null;
        this._ro = new ResizeObserver(([e]) => { const nw = Math.round(e.contentRect.width); if (!w) { w = nw; return; } if (nw === w) return; w = nw; clearTimeout(t); t = setTimeout(() => this.update(), 120); }); }
      if (this._ro) this._ro.observe(this);
      this.update();
    }
    disconnectedCallback() { this.removeEventListener('click', onRowClick); if (this._ro) this._ro.disconnect(); if (this._lbIo) { this._lbIo.disconnect(); this._lbIo = null; } clearTimeout(this._lbGaT); }
    async update() {
      const m = model(state.cart, this.cfg);
      const host = $('[data-lb-cu-row]', this) || this;
      if (!m.lines.length) { host.innerHTML = ''; this.hidden = true; return; }
      this.hidden = false;
      const token = (this._token = (this._token || 0) + 1);
      const step = await decide(m, this.cfg);
      if (token !== this._token || !this.isConnected) return; // a newer update won
      const key = step.node + ':' + (step.key || ''), place = this.dataset.placement || 'row';
      try { this._lbImp = GA.offer(step, m, this.cfg, place); } catch (e) { this._lbImp = null; }
      // One add sends several cart reads (the add's reply, the theme's, the gift modal's). A read that changes nothing visible
      // must not rebuild the row: that re-created the photo each time, and the row blinked like a reload.
      const sig = rowHTML(step, m, this.cfg, m.gb);
      if (sig === this._sig && host.querySelector('.lb-cu-row')) { state.shown[place] = m.gb; this._key = key; GA.arm(this); return; }
      const oldImg = host.querySelector('.lb-cu-th img');
      host.innerHTML = rowHTML(step, m, this.cfg, state.shown[place]);
      const newImg = host.querySelector('.lb-cu-th img');
      if (oldImg && newImg && oldImg.getAttribute('src') === newImg.getAttribute('src')) newImg.replaceWith(oldImg); // same photo: keep the decoded image
      fitRow(host, step.node === 'UNDO' ? null : goalHTML(m, this.cfg, true));
      state.shown[place] = m.gb;
      const row = host.querySelector('.lb-cu-row');
      if (row && key !== this._key) { row.classList.add('is-in'); emit('view', { node: step.node, key: step.key, placement: this.dataset.placement }); }
      this._key = key; this._sig = sig;
      tweenBars(host);
      LAST[place] = { sig, key, html: settled(host) };
      GA.arm(this);
    }
  }
  /** Each placement's last painted row, at rest (bar at its value, no entrance or reward animation). When the theme re-renders
   *  the drawer or /cart after a change, the new row starts from it instead of the half-empty Liquid first paint, so a new
   *  offer whose product data is still loading never flashes an empty line; the update then changes only what changed. */
  const LAST = {};
  function settled(host) {
    const c = host.cloneNode(true);
    $$('.lb-cu-fill[data-to]', c).forEach((f) => { f.style.width = f.dataset.to + '%'; f.parentNode.style.setProperty('--lb-cu-p', f.dataset.to); });
    $$('.is-in, .is-celebrating', c).forEach((el) => el.classList.remove('is-in', 'is-celebrating'));
    return c.innerHTML;
  }
  /** The fill's width and the bead's position (a transform on the meter's ::after) always move together. */
  function setFill(el, p) { el.style.width = p + '%'; if (el.parentNode) el.parentNode.style.setProperty('--lb-cu-p', p); }
  function tweenBars(scope) {
    const bars = $$('.lb-cu-fill[data-to]', scope);
    if (reduceMotion()) { bars.forEach((i) => setFill(i, i.dataset.to)); return; }
    requestAnimationFrame(() => requestAnimationFrame(() => bars.forEach((i) => setFill(i, i.dataset.to))));
  }
  function readJSON(el, sel) { const s = $(sel, el); if (!s) return null; try { return JSON.parse(s.textContent); } catch (e) { return null; } }
  function readConfig(el) {
    const c = readJSON(el, '[data-lb-cu-config]') || {};
    return Object.assign({}, DEFAULTS, c, { copy: Object.assign({}, DEFAULTS.copy, c.copy || {}), companions: Object.assign({}, DEFAULTS.companions, c.companions || {}),
      upgrades: Object.assign({}, DEFAULTS.upgrades, c.upgrades || {}), analytics: Object.assign({}, DEFAULTS.analytics, c.analytics || {}, { sinks: Object.assign({}, DEFAULTS.analytics.sinks, (c.analytics || {}).sinks || {}) }), pair: Object.assign({}, DEFAULTS.pair, c.pair || {}), giftVariantIds: (c.giftVariantIds || []).map(Number),
      thresholdBaseCents: Number(window.LB_FREE_GIFT_THRESHOLD_BASE_CENTS || c.thresholdBaseCents || DEFAULTS.thresholdBaseCents) });
  }
  const renderAll = () => { $$('lb-cart-upsell, lb-cart-bar').forEach((el) => el.update && el.update()); };

  /* ----------------------------------------------------------------- clicks */
  function onRowClick(e) {
    const b = e.target.closest('[data-lb-cu]'); if (!b || !this.contains(b)) return;
    const what = b.dataset.lbCu;
    if (what === 'offer') {
      const qs = document.getElementById('dialogQuickShop'); // <sht-dialog-quickshop> from snippets/quickshop.liquid, looked up by id (not the window named-access global)
      if (!qs || typeof qs.showModal !== 'function') { window.location.href = b.dataset.productUrl.split('?')[0]; return; } // no Quick Shop on this template: the product page
      state.pendingQS = { key: b.dataset.key, handle: b.dataset.handle, adds: state.adds, opened: false, ga: GA.open(this),
        swap: b.dataset.swap ? { key: b.dataset.swap, quantity: Number(b.dataset.swapQty) || 1 } : null };
      emit('open', { node: 'offer', key: b.dataset.key });
      qs.showModal(b); // reads data-product-url and fetches ?view=quick-view, exactly as a product card does
      return;
    }
    if (what === 'claim') { GA.open(this); emit('claim', { key: 'claim' }); return; } // free-gift-modal.js opens itself on [data-free-gift-progress-claim]
    if (what === 'undo') { undo(this); return; }
  }

  /* ------------------------------------------- Quick Shop: “not now” = close */
  // The theme's "Item added" panel opens after every Quick Shop add. From our row the shopper is already looking at the
  // bag (the drawer, or /cart with its own Checkout): there the panel only hides behind the drawer holding focus, or shows
  // a second Checkout. Close it and give focus back to the drawer. Elsewhere it stays the store's add feedback.
  function quietAddedPanel() {
    const noti = document.querySelector('sht-cart-noti');
    if (!noti || typeof noti.close !== 'function') return;
    const drawer = document.querySelector('sht-cart-drwr');
    const inDrawer = !!drawer && (drawer.hasAttribute('open') || drawer.classList.contains('active'));
    if (!inDrawer && !document.body.classList.contains('template-cart')) return;
    const shut = () => {
      if (noti.hasAttribute('hidden')) return false;
      noti.close();
      const wrap = inDrawer && drawer.querySelector('.js-cart-drawer-wrapper');
      if (wrap && window.SHTHelper && typeof SHTHelper.trapFocus === 'function') SHTHelper.trapFocus(wrap);
      return true;
    };
    if (shut()) return;
    const mo = new MutationObserver(() => { if (shut()) mo.disconnect(); });
    mo.observe(noti, { attributes: true, attributeFilter: ['hidden'] });
    setTimeout(() => mo.disconnect(), 2500);
  }
  function watchQuickShop() {
    const host = document.getElementById('dialogQuickShop');
    if (!host || host.__lbCu) return; host.__lbCu = true;
    new MutationObserver(() => {
      const p = state.pendingQS; if (!p) return;
      const open = !host.hasAttribute('hidden');
      if (open) { p.opened = true; return; }
      if (!p.opened) return;
      // Decide only when no add is still on its way. This reads the network, not any sizing UI, so it holds whatever
      // Quick Shop does between its dialog and the add (today: the Fitted picker, which has no dialog of its own).
      const t0 = Date.now();
      (function settle() {
        if (state.pendingQS !== p || !host.hasAttribute('hidden')) return;   // reopened, or another opener took over
        if (state.inflight > 0 && Date.now() - t0 < 4000) { setTimeout(settle, 120); return; }
        state.pendingQS = null;
        if (state.adds > p.adds) { GA.expect(p.ga, p.swap); emit('add', { key: p.key }); if (p.swap) swapKit(p); else { quietAddedPanel(); if (document.body.classList.contains('template-cart')) refreshCartPage(); } }
        else { decline(p.key); GA.decline(p.ga); emit('decline', { key: p.key }); renderAll(); } // “not now”: to the back of the queue, the next offer shows
      })();
    }).observe(host, { attributes: true, attributeFilter: ['hidden'] });
  }
  /** The set arrived from Quick Shop: the walk kit it replaces leaves the bag (one kit per set added). Our own request,
   *  so it is never read as a removal to undo. If the set is not in the bag after all, nothing is touched. */
  async function swapKit(p) {
    const cart = await fetch(url('cart.js'), { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
    const kit = cart && (cart.items || []).find((i) => i.key === p.swap.key), got = cart && (cart.items || []).some((i) => i.handle === p.handle);
    if (!kit || !got) { GA.settle(cart, true); quietAddedPanel(); if (document.body.classList.contains('template-cart')) refreshCartPage(); return; }
    const sections = themeSections().map((s) => s.section);
    const res = await fetch(url('cart/change.js'), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', [OWN]: '1' },
      body: JSON.stringify(Object.assign({ id: kit.key, quantity: Math.max(0, kit.quantity - 1) }, sections.length ? { sections, sections_url: window.location.pathname } : {})) })
      .then((r) => (r.ok ? r.json() : null)).catch(() => null);
    emit('swap', { key: p.key, kit: kit.handle, set: p.handle });
    quietAddedPanel();
    redrawTheme(res);
  }

  /* ------------------------------------------------------------------- undo */
  const OWN = 'X-LB-Cart-Upsell';
  function diffForUndo(before, after, cfg) {
    if (!before || !after) return null;
    const mb = model(before, cfg), ma = model(after, cfg);
    // Compared per variant, not per line key: Shopify gives a line a NEW key when its properties change (the Personalise popup
    // and “Change engraving” save the engraving that way), so by key an engraved collar looked removed. A piece is removed
    // only when fewer of that variant remain.
    const count = (items) => { const q = {}; (items || []).forEach((i) => { if (classify(i, cfg) !== 'gift') q[i.variant_id] = (q[i.variant_id] || 0) + i.quantity; }); return q; };
    const qb = count(before.items), qa = count(after.items), keysAfter = new Set((after.items || []).map((i) => i.key));
    const removed = [];
    Object.keys(qb).forEach((v) => {
      const left = qa[v] || 0; if (left >= qb[v]) return;
      const lines = (before.items || []).filter((i) => String(i.variant_id) === v && classify(i, cfg) !== 'gift');
      const item = lines.find((i) => !keysAfter.has(i.key)) || lines[0]; // the line that went (its engraving is what Undo restores)
      removed.push({ item, qty: qb[v] - left, all: left === 0 });
    });
    if (!removed.length || removed.length > 1) return null; // one deliberate removal; bulk changes (clear cart) are not undone
    const r = removed[0], kind = classify(r.item, cfg), C = cfg.copy, piece = cap(PIECE[kind] || 'item');
    const lostPair = mb.elQty >= cfg.pair.minQty && ma.elQty < cfg.pair.minQty;
    const lostTag = mb.hasGift && !ma.hasGift || (mb.unlocked && !ma.unlocked);
    // The engraved tag the gift modal withdraws below the goal comes back with the piece, engraving and all.
    const g = lostTag && (before.items || []).find((i) => classify(i, cfg) === 'gift');
    return { lines: [{ id: r.item.variant_id, quantity: r.qty, properties: r.item.properties || {} }], image: r.item.image,
      gift: g ? { id: g.variant_id, quantity: 1, properties: g.properties || {} } : null,
      text: r.all ? fill(C.removed, { piece }) : fill(C.lowered, { piece: piece.toLowerCase() }),
      note: lostTag && lostPair ? C.lostBoth : lostTag ? C.lostTag : lostPair ? C.lostPair : '',
      // for analytics: what went, and the order total before and after (Undo returns the cart to `totalBefore`)
      handle: r.item.handle, unit: Math.round(Number(r.item.final_line_price || 0) / Math.max(1, r.item.quantity)), qty: r.qty,
      lost: lostTag ? 'unlocks_gift' : lostPair ? 'pair_discount' : 'none', totalBefore: mb.total, totalAfter: ma.total };
  }
  // Kept in sessionStorage too: on /cart the gift modal reloads the page when it withdraws the tag, and the offer must survive it.
  const UNDO_KEY = 'lb-cu-undo';
  function setUndo(u, until) {
    clearTimeout(state.timer);
    state.undo = u ? Object.assign(u, { until: until || Date.now() + DEFAULTS.undoMs }) : null;
    try { if (state.undo) sessionStorage.setItem(UNDO_KEY, JSON.stringify(state.undo)); else sessionStorage.removeItem(UNDO_KEY); } catch (e) { /* memory only */ }
    if (state.undo) state.timer = setTimeout(() => { setUndo(null); renderAll(); }, state.undo.until - Date.now() + 30);
  }
  async function undo(host) {
    const u = state.undo; if (!u) return;
    GA.undo(host, u);
    setUndo(null); renderAll();
    emit('undo', { key: 'undo' });
    const sections = themeSections().map((s) => s.section);
    const add = (items, withSections) => fetch(url('cart/add.js'), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', [OWN]: '1' },
      body: JSON.stringify(withSections ? { items, sections, sections_url: window.location.pathname } : { items }) }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    normCart({ items: u.lines.concat(u.gift ? [u.gift] : []) }); // an Undo saved from an older cart read is normalised too
    if (!u.gift) { redrawTheme(await add(u.lines, true)); return; }
    // Piece first (the order is back over the goal), then the tag, unless the gift modal has not withdrawn it yet.
    const piece = await add(u.lines, false);
    const now = piece && await fetch(url('cart.js'), { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
    const cfg = state.cfg || readConfig(document.createElement('div'));
    const hasGift = now && (now.items || []).some((i) => classify(i, cfg) === 'gift');
    let res = piece && !hasGift ? await add([u.gift], true) : null;
    if (!res && sections.length) res = await fetch(url('cart') + '?sections=' + sections.join(',')) // no Accept header: see refreshCartPage
      .then((r) => (r.ok ? r.json() : null)).then((j) => (j ? { sections: j } : null)).catch(() => null);
    redrawTheme(res);
  }
  /** Quick Shop asks Shopify only for the drawer's markup, so on /cart an add from the row left the page's lines and total as they
   *  were. Fetch the page's sections and let the theme's own renderer draw them (no reload). */
  async function refreshCartPage() {
    const sections = themeSections().map((s) => s.section); if (!sections.length) return;
    // no Accept: application/json here — with it, /cart answers with the cart JSON instead of the sections
    const json = await fetch(url('cart') + '?sections=' + sections.join(',')) // Section Rendering API on /cart, from any page
      .then((r) => (r.ok ? r.json() : null)).catch(() => null);
    const f = document.querySelector('sht-cart-drwr-frm');
    if (json && f && typeof f.renderContents === 'function') { try { f.renderContents({ sections: json }); } catch (e) { /* the theme keeps its own markup */ } }
  }
  /** The sections the theme re-renders after a cart change (drawer + /cart), from its own drawer form. */
  function themeSections() { const f = document.querySelector('sht-cart-drwr-frm'); try { return f && f.getSectionsToRender ? f.getSectionsToRender() : []; } catch (e) { return []; } }
  function redrawTheme(res) {
    const f = document.querySelector('sht-cart-drwr-frm');
    if (res && res.sections && f && typeof f.renderContents === 'function') { try { f.renderContents(res); } catch (e) { /* fall through */ } }
    else if (document.body.classList.contains('template-cart')) { window.location.reload(); return; }
    refresh();
  }

  try { const u = JSON.parse(sessionStorage.getItem(UNDO_KEY) || 'null'); if (u && u.until > Date.now()) setUndo(u, u.until); else sessionStorage.removeItem(UNDO_KEY); } catch (e) { /* none */ }

  /** Shopify's Liquid writes `item.properties | json` as [key, value] pairs ([] when empty); /cart.js writes an object. Every cart
   *  is read as objects, or the gift and engraving properties go unseen and an Undo would post `properties: []` (Shopify: 400). */
  function normCart(cart) {
    (cart && cart.items || []).forEach((i) => { if (Array.isArray(i.properties)) i.properties = Object.fromEntries(i.properties.filter((p) => Array.isArray(p) && p.length === 2)); else if (!i.properties) i.properties = {}; });
    return cart;
  }

  /* ------------------------------------------------- observing cart changes */
  const MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  // Each shopper change carries its own snapshot of the cart from before it, and only that request's own response turns it into
  // an Undo. (A shared snapshot was consumed by whichever cart read landed first, e.g. the refresh after the gift modal's
  // cart:updated, which could still show the cart before the removal: the Undo was then lost.) A read that started before a
  // newer cart arrived is dropped, so an older cart never overwrites a newer one.
  let refreshT = null, applied = 0, pendingSnap = null;
  function refresh(delay, snap) {
    if (snap) pendingSnap = snap;
    clearTimeout(refreshT);
    refreshT = setTimeout(async () => {
      const gen = applied, mine = pendingSnap; pendingSnap = null;
      const cart = await fetch(url('cart.js'), { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
      if (!cart || !cart.items) { if (mine && !pendingSnap) pendingSnap = mine; return; }
      if (gen !== applied && !mine) return;
      onCart(cart, mine);
    }, delay == null ? 120 : delay);
  }
  function onCart(cart, snap) {
    const cfg = state.cfg || readConfig(document.createElement('div'));
    applied++; normCart(cart);
    if (snap) { const u = diffForUndo(snap, cart, cfg); if (u) setUndo(u); }
    state.cart = cart;
    GA.cart(cart);
    renderAll();
  }
  function observeRequest(urlLike, init, isOwn) {
    const u = typeof urlLike === 'string' ? urlLike : (urlLike && urlLike.url) || '';
    const hit = u.match(MUTATION); if (!hit) return null;
    const kind = hit[1];
    const snap = !isOwn && (kind === 'change' || kind === 'update') && state.cart ? JSON.parse(JSON.stringify(state.cart)) : null;
    return { kind, snap };
  }
  function patchNetwork() {
    if (window.fetch && !window.fetch.__lbCuPatched) {
      const orig = window.fetch;
      window.fetch = function (input, init) {
        const own = !!(init && init.headers && (init.headers[OWN] || (typeof init.headers.get === 'function' && init.headers.get(OWN))));
        const req = observeRequest(input, init, own), kind = req && req.kind, snap = req && req.snap;
        if (kind === 'add') state.inflight++;
        const p = orig.apply(this, arguments);
        if (kind === 'add') p.then(() => { state.inflight--; }, () => { state.inflight--; });
        if (kind) p.then((r) => { if (r && r.ok) { if (kind === 'add') state.adds++; return r.clone().json().then((j) => { if (j && j.items && j.token) onCart(j, snap); else refresh(0, snap); }); } refresh(); }).catch(() => refresh());
        return p;
      };
      window.fetch.__lbCuPatched = true;
    }
    if (window.XMLHttpRequest && !XMLHttpRequest.prototype.__lbCuPatched) {
      const open = XMLHttpRequest.prototype.open, send = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (m, u) { this.__lbCuUrl = u; return open.apply(this, arguments); };
      XMLHttpRequest.prototype.send = function () {
        const req = observeRequest(this.__lbCuUrl, null, false), kind = req && req.kind;
        if (kind === 'add') state.inflight++;
        if (kind) this.addEventListener('loadend', () => { if (kind === 'add') state.inflight--; const ok = this.status >= 200 && this.status < 300; if (ok && kind === 'add') state.adds++; refresh(0, ok ? req.snap : null); });
        return send.apply(this, arguments);
      };
      XMLHttpRequest.prototype.__lbCuPatched = true;
    }
    // the theme and the gift modal also announce changes
    ['cart:updated', 'cart:refresh', 'cart:change', 'free-gift:removed'].forEach((ev) => document.addEventListener(ev, () => refresh(200)));
  }

  /* ------------------------------------------------ /cart phone sticky bar */
  class LBCartBar extends HTMLElement {
    connectedCallback() {
      this.cfg = state.cfg = state.cfg || readConfig(this);
      const embedded = readJSON(this, '[data-lb-cu-cart]');
      if (embedded) state.cart = normCart(embedded);
      this.update();
      // steps aside only once the page's own Checkout is fully on screen: never two Checkouts, never none
      const target = document.querySelector('#mainCart .cart__checkout [name="checkout"]');
      if (target && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([e]) => this.classList.toggle('is-off', e.isIntersecting && e.intersectionRatio > 0.99), { threshold: [0, 1] });
        this.io.observe(target);
      }
      // the row at the top of the page already says the goal: the bar keeps only its total until that row scrolls away
      const row = document.querySelector('lb-cart-upsell[data-placement="page-top"]');
      if (row && 'IntersectionObserver' in window) {
        this.rowIo = new IntersectionObserver(([e]) => this.classList.toggle('is-row-in-view', e.isIntersecting));
        this.rowIo.observe(row);
      }
    }
    disconnectedCallback() { if (this.io) this.io.disconnect(); if (this.rowIo) this.rowIo.disconnect(); }
    update() {
      const m = model(state.cart, this.cfg);
      this.hidden = !m.lines.length;
      const total = $('[data-lb-cu-total]', this), goal = $('[data-lb-cu-goal]', this);
      if (total) total.textContent = money(m.total);
      if (goal) goal.innerHTML = goalHTML(m, this.cfg, true);
      const fillEl = $('.lb-cu-fill', this);
      if (fillEl) { const pct = m.giftOn ? pctOf(m.gb, m) : 50, from = state.shown.bar == null ? pct : pctOf(state.shown.bar, m);
        setFill(fillEl, from); fillEl.dataset.to = pct; state.shown.bar = m.gb; this.classList.toggle('is-unlocked', m.unlocked); tweenBars(this); }
    }
  }

  /* ------------------------------------------------------------------- boot */
  if (!customElements.get('lb-cart-upsell')) customElements.define('lb-cart-upsell', LBCartUpsell);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => renderAll()); // widths change when Lora/Roboto arrive: fit again
  if (!customElements.get('lb-cart-bar')) customElements.define('lb-cart-bar', LBCartBar);
  patchNetwork();
  const ready = () => { watchQuickShop(); GA.boot(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready); else ready();
  window.addEventListener('load', ready);

  window.LBCartUpsell = { refresh: () => refresh(0), state, mem, model, decide, classify, project, productSteps, upgradeHandle, analytics: GA, version: VERSION };
})();
